// src/components/Laporan.jsx
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const num = (v) => Number(v) || 0;
const rupiah = (n) => "Rp " + num(n).toLocaleString("id-ID");

const emptyForm = {
  neraca: {
    kas_bank: 0, deposito_emas: 0, piutang_lain: 0,
    inventaris: 0, akumulasi_penyusutan: 0,
    utang_dana_shu: 0, utang_pajak_badan: 0, cadangan_modal: 0, shu_tahun: 0,
  },
  rugi_laba: {
    pendapatan_jasa_simpan_pinjam: 0, shu_dibagikan: 0, pendapatan_jasa_giro: 0,
    insentif_pengurus: 0, insentif_petugas: 0, jasa_anggota_pensiun: 0,
    administrasi_bank: 0, akta_koperasi: 0, pajak_rekening_bank: 0,
    transport: 0, sembako_lebaran: 0, donation_rumah_yatim: 0,
    dana_sosial_sponsor: 0, biaya_pajak: 0,
  },
  dana_sosial: [],
  jasa_kepesertaan: [],
  sembako: [],
};

// Field input angka
const MoneyInput = ({ label, value, onChange, hint }) => (
  <div>
    <label className="text-xs text-slate-500">{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
    />
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

// Baris label + nilai (untuk total)
const TotalRow = ({ label, value, strong }) => (
  <div className="flex justify-between py-1.5 text-sm">
    <span className={strong ? "font-semibold text-slate-800" : "text-slate-600"}>{label}</span>
    <span className={strong ? "font-semibold text-slate-800" : "text-slate-700"}>{rupiah(value)}</span>
  </div>
);

export default function Laporan({ api }) {
  const [year, setYear] = useState(2025);
  const [form, setForm] = useState(emptyForm);
  const [auto, setAuto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setMsg(null);
      const res = await api.get("/laporan", { params: { year } });
      setAuto(res.data.auto);
      const saved = res.data.saved;
      if (saved) {
        setForm({ ...emptyForm, ...saved,
          neraca: { ...emptyForm.neraca, ...(saved.neraca || {}) },
          rugi_laba: { ...emptyForm.rugi_laba, ...(saved.rugi_laba || {}) },
          dana_sosial: saved.dana_sosial || [],
          jasa_kepesertaan: saved.jasa_kepesertaan || [],
          sembako: saved.sembako || [],
        });
      } else {
        // Belum ada: prefill pendapatan jasa dgn total margin
        setForm({
          ...emptyForm,
          rugi_laba: { ...emptyForm.rugi_laba, pendapatan_jasa_simpan_pinjam: res.data.auto?.total_margin || 0 },
        });
      }
    } catch (e) {
      console.error(e);
      setError("Gagal memuat laporan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      setMsg(null);
      setError(null);
      await api.post("/laporan", { year, data: form });
      setMsg(`Laporan tahun ${year} berhasil disimpan.`);
    } catch (e) {
      console.error(e);
      setError("Gagal menyimpan laporan.");
    } finally {
      setSaving(false);
    }
  };

  const setNeraca = (k, v) => setForm((f) => ({ ...f, neraca: { ...f.neraca, [k]: v } }));
  const setRL = (k, v) => setForm((f) => ({ ...f, rugi_laba: { ...f.rugi_laba, [k]: v } }));

  // List dinamis
  const addRow = (key, blank) => setForm((f) => ({ ...f, [key]: [...f[key], blank] }));
  const updRow = (key, i, field, val) =>
    setForm((f) => ({ ...f, [key]: f[key].map((r, idx) => (idx === i ? { ...r, [field]: val } : r)) }));
  const delRow = (key, i) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  // ── Hitung total ──
  const a = auto || {};
  const n = form.neraca;
  const rl = form.rugi_laba;

  const totalAktivaLancar = num(n.kas_bank) + num(a.pinjaman_anggota) + num(n.piutang_lain) + num(n.deposito_emas);
  const nilaiBuku = num(n.inventaris) - num(n.akumulasi_penyusutan);
  const totalAktiva = totalAktivaLancar + nilaiBuku;
  const totalKewajiban = num(a.simpanan_sukarela) + num(n.utang_dana_shu) + num(n.utang_pajak_badan);
  const totalEkuitas = num(a.simpanan_pokok) + num(a.simpanan_wajib) + num(n.cadangan_modal) + num(n.shu_tahun);
  const totalPasiva = totalKewajiban + totalEkuitas;
  const selisihNeraca = totalAktiva - totalPasiva;

  const totalPendapatan = (num(rl.pendapatan_jasa_simpan_pinjam) - num(rl.shu_dibagikan)) + num(rl.pendapatan_jasa_giro);
  const subOperasional = num(rl.insentif_pengurus) + num(rl.insentif_petugas) + num(rl.jasa_anggota_pensiun) + num(rl.administrasi_bank) + num(rl.akta_koperasi) + num(rl.pajak_rekening_bank);
  const subUmum = num(rl.transport);
  const subOrganisasi = num(rl.sembako_lebaran) + num(rl.donation_rumah_yatim) + num(rl.dana_sosial_sponsor);
  const totalBiaya = subOperasional + subUmum + subOrganisasi;
  const shuSebelumPajak = totalPendapatan - totalBiaya;
  const shuSetelahPajak = shuSebelumPajak - num(rl.biaya_pajak);

  const sumList = (arr) => arr.reduce((s, r) => s + num(r.jumlah), 0);

  // ── Export Excel (multi-sheet) ──
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Neraca
    const neraca = [
      ["KOPERASI SIMPAN PINJAM KARYAWAN PT.CROWN WORLDWIDE INDONESIA"],
      ["NERACA", `31 DESEMBER ${year}`],
      [],
      ["AKTIVA", "", "KEWAJIBAN & EKUITAS", ""],
      ["Kas/Bank", num(n.kas_bank), "Simpanan Sukarela", num(a.simpanan_sukarela)],
      ["Pinjaman kepada anggota", num(a.pinjaman_anggota), "Utang dana bagian SHU", num(n.utang_dana_shu)],
      ["Piutang Lain-lain", num(n.piutang_lain), "Utang Pajak Badan", num(n.utang_pajak_badan)],
      ["Deposito Emas", num(n.deposito_emas), "Total Kewajiban", totalKewajiban],
      ["Total Aktiva Lancar", totalAktivaLancar, "", ""],
      ["Inventaris", num(n.inventaris), "Simpanan Pokok", num(a.simpanan_pokok)],
      ["Akumulasi Penyusutan", num(n.akumulasi_penyusutan), "Simpanan Wajib", num(a.simpanan_wajib)],
      ["Nilai Buku", nilaiBuku, "Cadangan/Modal", num(n.cadangan_modal)],
      ["", "", `SHU ${year}`, num(n.shu_tahun)],
      ["", "", "Total Ekuitas", totalEkuitas],
      ["TOTAL AKTIVA", totalAktiva, "TOTAL KEWAJIBAN & EKUITAS", totalPasiva],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(neraca), "Neraca");

    // Rugi Laba
    const rugiLaba = [
      ["KOPERASI SIMPAN PINJAM KARYAWAN PT.CROWN WORLDWIDE INDONESIA"],
      ["LAPORAN PERHITUNGAN HASIL USAHA", `PERIODE JAN-DES ${year}`],
      [],
      ["PARTISIPASI ANGGOTA", ""],
      ["Pendapatan jasa simpan pinjam", num(rl.pendapatan_jasa_simpan_pinjam)],
      ["Less: SHU yang dibagikan ke anggota", num(rl.shu_dibagikan)],
      ["Pendapatan jasa giro/bunga rekening", num(rl.pendapatan_jasa_giro)],
      ["TOTAL PENDAPATAN", totalPendapatan],
      [],
      ["BEBAN OPERASIONAL", ""],
      ["Insentif pengurus koperasi", num(rl.insentif_pengurus)],
      ["Insentif petugas keuangan dan kurir", num(rl.insentif_petugas)],
      ["Jasa anggota yang pensiun", num(rl.jasa_anggota_pensiun)],
      ["Administrasi bank", num(rl.administrasi_bank)],
      ["Pengurusan Akta Koperasi", num(rl.akta_koperasi)],
      ["Pajak rekening bank", num(rl.pajak_rekening_bank)],
      ["Sub Total Operasional", subOperasional],
      [],
      ["BIAYA UMUM & ADMINISTRASI", ""],
      ["Transport pengambilan cek", num(rl.transport)],
      [],
      ["BEBAN ORGANISASI", ""],
      ["Sembako lebaran anggota", num(rl.sembako_lebaran)],
      ["Donation rumah yatim", num(rl.donation_rumah_yatim)],
      ["Dana Sosial & Sponsor", num(rl.dana_sosial_sponsor)],
      ["Sub Total Organisasi", subOrganisasi],
      [],
      ["TOTAL BIAYA", totalBiaya],
      ["SISA HASIL USAHA SEBELUM PAJAK", shuSebelumPajak],
      ["Biaya Pajak", num(rl.biaya_pajak)],
      ["SISA HASIL USAHA SETELAH PAJAK", shuSetelahPajak],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rugiLaba), "Rugi Laba");

    // Dana Sosial
    const ds = [["DANA SOSIAL"], ["No", "Keterangan", "Jumlah"]];
    form.dana_sosial.forEach((r, i) => ds.push([i + 1, r.keterangan, num(r.jumlah)]));
    ds.push(["", "TOTAL", sumList(form.dana_sosial)]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ds), "Dana Sosial");

    // Jasa Kepesertaan & Sembako
    const jk = [["JASA KEPESERTAAN (Anggota Pensiun/Keluar)"], ["No", "Nama", "Jumlah"]];
    form.jasa_kepesertaan.forEach((r, i) => jk.push([i + 1, r.nama, num(r.jumlah)]));
    jk.push(["", "TOTAL", sumList(form.jasa_kepesertaan)]);
    jk.push([]);
    jk.push(["SEMBAKO"], ["No", "Nama", "Jumlah"]);
    form.sembako.forEach((r, i) => jk.push([i + 1, r.nama, num(r.jumlah)]));
    jk.push(["", "TOTAL", sumList(form.sembako)]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(jk), "Jasa Kepesertaan & Sembako");

    XLSX.writeFile(wb, `Laporan_Koperasi_${year}.xlsx`);
  };

  // ── Export PDF ──
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(13);
    doc.text(`Laporan Koperasi ${year}`, 14, 15);
    doc.setFontSize(11);
    doc.text("Neraca", 14, 24);
    autoTable(doc, {
      startY: 28,
      head: [["AKTIVA", "Rp", "KEWAJIBAN & EKUITAS", "Rp"]],
      body: [
        ["Kas/Bank", rupiah(n.kas_bank), "Simpanan Sukarela", rupiah(a.simpanan_sukarela)],
        ["Pinjaman ke anggota", rupiah(a.pinjaman_anggota), "Utang dana SHU", rupiah(n.utang_dana_shu)],
        ["Piutang Lain-lain", rupiah(n.piutang_lain), "Utang Pajak Badan", rupiah(n.utang_pajak_badan)],
        ["Deposito Emas", rupiah(n.deposito_emas), "Simpanan Pokok", rupiah(a.simpanan_pokok)],
        ["Nilai Buku Inventaris", rupiah(nilaiBuku), "Simpanan Wajib", rupiah(a.simpanan_wajib)],
        ["", "", "Cadangan/Modal", rupiah(n.cadangan_modal)],
        ["", "", `SHU ${year}`, rupiah(n.shu_tahun)],
      ],
      foot: [["TOTAL AKTIVA", rupiah(totalAktiva), "TOTAL KWJB & EKUITAS", rupiah(totalPasiva)]],
      styles: { fontSize: 8 }, headStyles: { fillColor: [59, 130, 246] },
      footStyles: { fillColor: [226, 232, 240], textColor: 20, fontStyle: "bold" },
    });

    let y = doc.lastAutoTable.finalY + 8;
    doc.text("Rugi Laba", 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [["Uraian", "Jumlah"]],
      body: [
        ["Total Pendapatan", rupiah(totalPendapatan)],
        ["Total Biaya", rupiah(totalBiaya)],
        ["SHU Sebelum Pajak", rupiah(shuSebelumPajak)],
        ["Biaya Pajak", rupiah(rl.biaya_pajak)],
        ["SHU Setelah Pajak", rupiah(shuSetelahPajak)],
      ],
      styles: { fontSize: 9 }, headStyles: { fillColor: [16, 185, 129] },
    });

    doc.save(`Laporan_Koperasi_${year}.pdf`);
  };

  const ListSection = ({ title, listKey, fieldName, blank }) => (
    <div className="rounded-2xl border bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-700">{title}</h3>
        <button
          onClick={() => addRow(listKey, blank)}
          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700"
        >
          + Tambah
        </button>
      </div>
      {form[listKey].length === 0 ? (
        <p className="text-sm text-slate-400">Belum ada data</p>
      ) : (
        <div className="space-y-2">
          {form[listKey].map((r, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                placeholder={fieldName}
                value={r[fieldName] || ""}
                onChange={(e) => updRow(listKey, i, fieldName, e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              />
              <input
                type="number"
                placeholder="Jumlah"
                value={r.jumlah || ""}
                onChange={(e) => updRow(listKey, i, "jumlah", e.target.value)}
                className="w-40 border rounded-lg px-3 py-2 text-sm"
              />
              <button
                onClick={() => delRow(listKey, i)}
                className="px-2 py-2 rounded-lg bg-red-100 text-red-600 text-xs hover:bg-red-200"
              >
                Hapus
              </button>
            </div>
          ))}
          <div className="flex justify-end pt-2 border-t text-sm font-semibold text-slate-700">
            Total: {rupiah(sumList(form[listKey]))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold text-slate-800">Laporan Koperasi</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Tahun</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-28 border rounded-xl px-3 py-2 text-sm"
          />
          <button onClick={load} className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700">
            Muat
          </button>
        </div>
      </div>

      {msg && <div className="rounded-2xl bg-green-50 border border-green-200 p-3 text-green-700 text-sm">{msg}</div>}
      {error && <div className="rounded-2xl bg-red-50 border border-red-200 p-3 text-red-700 text-sm">{error}</div>}
      {loading && <div className="text-slate-400 text-sm">Memuat...</div>}

      {/* Nilai otomatis dari DB */}
      {auto && (
        <div className="rounded-2xl border bg-slate-50 p-4">
          <p className="text-xs font-semibold text-slate-500 mb-2">Otomatis dari Database (tahun {year})</p>
          <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Simpanan Pokok:</span> <b>{rupiah(auto.simpanan_pokok)}</b></div>
            <div><span className="text-slate-500">Simpanan Wajib:</span> <b>{rupiah(auto.simpanan_wajib)}</b></div>
            <div><span className="text-slate-500">Simpanan Sukarela:</span> <b>{rupiah(auto.simpanan_sukarela)}</b></div>
            <div><span className="text-slate-500">Pinjaman ke Anggota:</span> <b>{rupiah(auto.pinjaman_anggota)}</b></div>
          </div>
        </div>
      )}

      {/* NERACA */}
      <div className="rounded-2xl border bg-white p-5">
        <h2 className="font-semibold text-slate-700 mb-3">Neraca</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">AKTIVA (input manual)</p>
            <div className="space-y-3">
              <MoneyInput label="Kas / Bank" value={n.kas_bank} onChange={(v) => setNeraca("kas_bank", v)} />
              <MoneyInput label="Deposito Emas" value={n.deposito_emas} onChange={(v) => setNeraca("deposito_emas", v)} />
              <MoneyInput label="Piutang Lain-lain" value={n.piutang_lain} onChange={(v) => setNeraca("piutang_lain", v)} />
              <MoneyInput label="Inventaris" value={n.inventaris} onChange={(v) => setNeraca("inventaris", v)} />
              <MoneyInput label="Akumulasi Penyusutan" value={n.akumulasi_penyusutan} onChange={(v) => setNeraca("akumulasi_penyusutan", v)} />
            </div>
            <div className="mt-3 border-t pt-2">
              <TotalRow label="Pinjaman ke anggota (auto)" value={a.pinjaman_anggota} />
              <TotalRow label="Total Aktiva Lancar" value={totalAktivaLancar} />
              <TotalRow label="Nilai Buku Inventaris" value={nilaiBuku} />
              <TotalRow label="TOTAL AKTIVA" value={totalAktiva} strong />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">KEWAJIBAN & EKUITAS (input manual)</p>
            <div className="space-y-3">
              <MoneyInput label="Utang Dana Bagian SHU" value={n.utang_dana_shu} onChange={(v) => setNeraca("utang_dana_shu", v)} />
              <MoneyInput label="Utang Pajak Badan" value={n.utang_pajak_badan} onChange={(v) => setNeraca("utang_pajak_badan", v)} />
              <MoneyInput label="Cadangan / Modal" value={n.cadangan_modal} onChange={(v) => setNeraca("cadangan_modal", v)} />
              <MoneyInput label={`SHU ${year} (dari menu SHU)`} value={n.shu_tahun} onChange={(v) => setNeraca("shu_tahun", v)} />
            </div>
            <div className="mt-3 border-t pt-2">
              <TotalRow label="Simpanan Sukarela (auto)" value={a.simpanan_sukarela} />
              <TotalRow label="Simpanan Pokok (auto)" value={a.simpanan_pokok} />
              <TotalRow label="Simpanan Wajib (auto)" value={a.simpanan_wajib} />
              <TotalRow label="Total Kewajiban" value={totalKewajiban} />
              <TotalRow label="Total Ekuitas" value={totalEkuitas} />
              <TotalRow label="TOTAL KWJB & EKUITAS" value={totalPasiva} strong />
            </div>
          </div>
        </div>
        <div className={`mt-3 text-sm font-medium ${Math.abs(selisihNeraca) < 1 ? "text-green-600" : "text-orange-600"}`}>
          {Math.abs(selisihNeraca) < 1 ? "✓ Neraca balance" : `Selisih neraca: ${rupiah(selisihNeraca)} (cek kembali angka)`}
        </div>
      </div>

      {/* RUGI LABA */}
      <div className="rounded-2xl border bg-white p-5">
        <h2 className="font-semibold text-slate-700 mb-3">Rugi Laba</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-500">PENDAPATAN</p>
            <MoneyInput label="Pendapatan jasa simpan pinjam" value={rl.pendapatan_jasa_simpan_pinjam} onChange={(v) => setRL("pendapatan_jasa_simpan_pinjam", v)} hint={`Saran (total margin): ${rupiah(a.total_margin)}`} />
            <MoneyInput label="Less: SHU dibagikan ke anggota" value={rl.shu_dibagikan} onChange={(v) => setRL("shu_dibagikan", v)} />
            <MoneyInput label="Pendapatan jasa giro / bunga rekening" value={rl.pendapatan_jasa_giro} onChange={(v) => setRL("pendapatan_jasa_giro", v)} />
            <TotalRow label="TOTAL PENDAPATAN" value={totalPendapatan} strong />
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-500">BEBAN</p>
            <MoneyInput label="Insentif pengurus koperasi" value={rl.insentif_pengurus} onChange={(v) => setRL("insentif_pengurus", v)} />
            <MoneyInput label="Insentif petugas keuangan & kurir" value={rl.insentif_petugas} onChange={(v) => setRL("insentif_petugas", v)} />
            <MoneyInput label="Jasa anggota yang pensiun" value={rl.jasa_anggota_pensiun} onChange={(v) => setRL("jasa_anggota_pensiun", v)} />
            <MoneyInput label="Administrasi bank" value={rl.administrasi_bank} onChange={(v) => setRL("administrasi_bank", v)} />
            <MoneyInput label="Pengurusan Akta Koperasi" value={rl.akta_koperasi} onChange={(v) => setRL("akta_koperasi", v)} />
            <MoneyInput label="Pajak rekening bank" value={rl.pajak_rekening_bank} onChange={(v) => setRL("pajak_rekening_bank", v)} />
            <MoneyInput label="Transport pengambilan cek" value={rl.transport} onChange={(v) => setRL("transport", v)} />
            <MoneyInput label="Sembako lebaran anggota" value={rl.sembako_lebaran} onChange={(v) => setRL("sembako_lebaran", v)} />
            <MoneyInput label="Donation rumah yatim" value={rl.donation_rumah_yatim} onChange={(v) => setRL("donation_rumah_yatim", v)} />
            <MoneyInput label="Dana Sosial & Sponsor" value={rl.dana_sosial_sponsor} onChange={(v) => setRL("dana_sosial_sponsor", v)} />
          </div>
        </div>
        <div className="mt-3 border-t pt-2 grid md:grid-cols-2 gap-x-6">
          <TotalRow label="Total Biaya" value={totalBiaya} />
          <TotalRow label="SHU Sebelum Pajak" value={shuSebelumPajak} />
          <div><MoneyInput label="Biaya Pajak" value={rl.biaya_pajak} onChange={(v) => setRL("biaya_pajak", v)} /></div>
          <TotalRow label="SHU SETELAH PAJAK" value={shuSetelahPajak} strong />
        </div>
      </div>

      {/* LIST SECTIONS */}
      <ListSection title="Dana Sosial" listKey="dana_sosial" fieldName="keterangan" blank={{ keterangan: "", jumlah: 0 }} />
      <ListSection title="Jasa Kepesertaan (Anggota Pensiun/Keluar)" listKey="jasa_kepesertaan" fieldName="nama" blank={{ nama: "", jumlah: 0 }} />
      <ListSection title="Sembako" listKey="sembako" fieldName="nama" blank={{ nama: "", jumlah: 0 }} />

      {/* ACTIONS */}
      <div className="flex flex-wrap gap-3 sticky bottom-4">
        <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 shadow">
          {saving ? "Menyimpan..." : "Simpan Laporan"}
        </button>
        <button onClick={exportExcel} className="px-5 py-2.5 rounded-xl bg-green-600 text-white text-sm hover:bg-green-700 shadow">
          Export Excel
        </button>
        <button onClick={exportPDF} className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700 shadow">
          Export PDF
        </button>
      </div>
    </div>
  );
}
